import { useThrottleFn } from '@vueuse/core';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { useEventEmitter } from '@/composable/useEventEmitter';
import { addHour, getNowWithGMT } from '@/core/lib/date';
import { defineContext } from '@/core/lib/defineContext';
import { useEvents } from '@/core/oddsApi/composable/useEvents';
import { getEventPresetFilterInputType, getOddsCategory } from '@/core/oddsApi/helpers';
import { EnumEventDateFilterType, EnumPresetFilterType } from '@/core/oddsApi/oddsApiType';
import type { IEvent } from '@/interface/IEvent';
import { EventDateType, MarketPage, OddsDaylightType, OddsSorting, SiteStyle, SportType } from '@/interface/enum';
import { useAsiaSportMarketInfoStore } from '@/store/asiaSportMarketInfoStore';
import { useCustomerStore } from '@/store/customerStore';
import { useOddsDisplaySettingsStore } from '@/store/oddsDisplaySettingsStore';
import { useSelectedLeagueStore } from '@/store/selectedLeagueStore';
import { useToggleStore } from '@/store/toggleStore';

const HoursMapping: Partial<Record<OddsDaylightType, number>> = {
    [OddsDaylightType.Within3Hours]: 3,
    [OddsDaylightType.Within6Hours]: 6,
};

type OddsDisplayBlockFilterRefs = {
    leagueIds?: Ref<number[]>;
    eventIds?: Ref<number[]>;
    leagueNames?: Ref<string[]>;
};

export const OddsDisplayBlockContext = defineContext('OddsDisplayBlock', (filterRefs?: OddsDisplayBlockFilterRefs) => {
    const { selectedLeagueIds } = storeToRefs(useSelectedLeagueStore());

    const { oddsSorting } = storeToRefs(useCustomerStore());

    const {
        sportType,
        marketPage,
        eventDate,
    } = useAsiaSportMarketInfoStore();

    const { oddsDisplayMode } = useOddsDisplaySettingsStore();
    const { isMyFavorite } = storeToRefs(useAsiaSportMarketInfoStore());
    const { isUseSoccerTodayExpand } = storeToRefs(useToggleStore(SiteStyle.Asia));

    // Soccer Today tab uses TodayExpanded (when enabled by toggle) so the list also
    // includes next-day matches within the backend's expanded window.
    const isSoccerTodayExpanded = computed(() => isUseSoccerTodayExpand.value
        && marketPage === MarketPage.Today
        && sportType === SportType.Soccer);

    const presetFilter = computed(() => (isSoccerTodayExpanded.value
        ? { presetFilter: EnumPresetFilterType.All, date: EnumEventDateFilterType.TodayExpanded }
        : getEventPresetFilterInputType(marketPage, eventDate)));

    const {
        daylightType,
        filteredLeagueIds: _filteredLeagueIds,
        filteredEventIds: _filteredEventIds,
        filteredLeagueNames: _filteredLeagueNames,
        favoriteCount,
    } = storeToRefs(useAsiaSportMarketInfoStore());

    const isFilterNotEmpty = computed(() => filterRefs?.leagueIds?.value
        || filterRefs?.eventIds?.value
        || filterRefs?.leagueNames?.value);
    const filteredLeagueIds = computed(() => (isFilterNotEmpty.value ? (filterRefs?.leagueIds?.value ?? null) : _filteredLeagueIds.value));
    const filteredEventIds = computed(() => (isFilterNotEmpty.value ? (filterRefs?.eventIds?.value ?? null) : _filteredEventIds.value));
    const filteredLeagueNames = computed(() => (isFilterNotEmpty.value ? (filterRefs?.leagueNames?.value ?? null) : _filteredLeagueNames.value));

    const isEnabledEvents = computed(() => {
        if (isMyFavorite.value) return favoriteCount.value > 0;
        if (marketPage === MarketPage.EarlyMarket) return eventDate !== EventDateType.Unknown;

        return true;
    });

    const { events, loaded, loading, eventsError, refetch } = useEvents({
        sportType,
        presetFilter,
        oddsCategory: getOddsCategory(marketPage),
        eventIds: filteredEventIds,
        leagueIds: filteredLeagueIds,
        leagueNames: filteredLeagueNames,
        isActive: isEnabledEvents,
        isResetResults: !isMyFavorite.value,
    });

    const throttledRefetch = useThrottleFn(refetch, 5000, false, true);
    const eventEmitter = useEventEmitter();
    eventEmitter.on('oddsDisplay:refreshEventList', () => {
        throttledRefetch();
    });

    const sortedEvents = computed<IEvent[]>(() => orderBy(events.value, (event: IEvent) => (oddsSorting.value === OddsSorting.ShowTime
        ? event.showTime
        : event.eventCode)));

    const filteredEvents = computed<IEvent[]>(() => (selectedLeagueIds.value.length > 0
        ? sortedEvents.value.filter(x => selectedLeagueIds.value.includes(x.league.id))
        : sortedEvents.value));

    const shouldFilterByDaylight = computed(() => sportType === SportType.Soccer
        && marketPage === MarketPage.Today
        && [OddsDaylightType.Within3Hours, OddsDaylightType.Within6Hours].includes(daylightType.value));

    const displayEvents = computed<IEvent[]>(() => {
        if (!isEnabledEvents.value) return [];
        if (!shouldFilterByDaylight.value) return filteredEvents.value;

        return filteredEvents.value.filter((x) => {
            const serverTimeNow = getNowWithGMT(-4);
            const threshold = addHour(serverTimeNow, HoursMapping[daylightType.value] ?? 0);
            return x.absKickOffTime <= threshold;
        });
    });

    return {
        sportType,
        marketPage,
        oddsDisplayMode,
        loaded,
        loading,
        eventsError,
        refetch,
        events,
        displayEvents,
        isEnabledEvents,
    };
});
